/**
 * Submit handler for the "create workers request" path.
 *
 * Triggered when the form has no existing `workersRequest` (fresh creation, possibly
 * from a template). Expands each post's `count` into N identical API payloads.
 */
import { useCreateMutation } from '../../../../../queries/workers-requests'
import {
  WorkersRequestCreateFormValues,
  WorkersRequestCreatePostValues,
} from '../workers-request-form.types'
import { buildWorkPeriodsPayload } from './build-work-periods-payload'

/**
 * Dependencies required by the create-submit handler.
 */
export interface SubmitCreateDeps {
  createWorkersRequestMutation: ReturnType<typeof useCreateMutation>
}

/**
 * Flattens the posts array by expanding `count` into N identical entries and
 * serialising tags to their ids.
 */
const buildPostsPayload = (posts: WorkersRequestCreateFormValues['posts']) =>
  posts.reduce(
    (acc, current) => {
      const { count, tags, ...post } = current
      for (let i = 0; i < count; i++) {
        acc.push({
          ...post,
          tags: tags?.map(tag => tag._id) || [],
        })
      }
      return acc
    },
    [] as Array<Omit<WorkersRequestCreatePostValues, 'count' | 'tags'> & { tags: string[] }>,
  )

/**
 * Submits a new workers request.
 *
 * @param values - Validated Formik values (guaranteed to be `WorkersRequestCreateFormValues`).
 * @param deps - Mutation hooks injected from the component.
 */
export const submitCreate = (
  values: WorkersRequestCreateFormValues,
  deps: SubmitCreateDeps,
): void => {
  const { interimAgency, missionData, posts } = values
  const workPeriods = buildWorkPeriodsPayload(values)

  deps.createWorkersRequestMutation.mutate({
    interimAgency,
    missionData: {
      ...missionData,
      workPeriods,
    },
    posts: buildPostsPayload(posts),
  })
}
